import { AppDispatch, RootState } from "../../app/store";
import { User } from "../user/user";
import {
  fetchConversationMessages,
  fetchConversations,
  fetchInboxSummary,
} from "./messagingActions";
import { planReadReceipt } from "./readReceipt";
import {
  applyReadReceipt,
  applyServerAttachments,
  resetMessagesByConvId,
  selectAllMessages,
  selectConversationById,
  setHasUnreadMessages,
  setMessages,
  upsertConversation,
} from "./messagingSlice";
import {
  activeInboxRefreshRequest,
  openInboxRefreshRequest,
} from "./inboxTabs";
import {
  ChatMessage,
  Conversation,
  ConversationStatus,
  MessageType,
} from "./types/queries";
import { transferrerDisplayName } from "./transferConversation";
import {
  ConversationAssignedPayload,
  ConversationMessageAttachmentsUpdatedPayload,
  ConversationMessagesReadPayload,
  ConversationStatusUpdatedPayload,
  ConversationTransferredPayload,
  MessagingEventPayload,
  MessagingEventType,
  NewConversationMessagePayload,
  SocketEvent,
} from "./types/socket";

export type MessagingSocketHandlerContext = {
  dispatch: AppDispatch;
  getState: () => RootState;
  user: User;
  isMessagingPath: () => boolean;
  sendMessage: (text: string, type: "success" | "info") => void;
  t: (key: string, options?: { name: string }) => string;
};

export const handleMessagingSocketEvent = (
  event: SocketEvent<MessagingEventPayload>,
  {
    dispatch,
    getState,
    user,
    isMessagingPath,
    sendMessage,
    t,
  }: MessagingSocketHandlerContext,
) => {
  switch (event.type) {
    case MessagingEventType.NEW_CONVERSATION_MESSAGE: {
      const typedEvent = event as SocketEvent<NewConversationMessagePayload>;

      const state = getState();
      const conv = selectConversationById(
        state,
        typedEvent.payload.conversationId,
      );
      const isSenderNotMe = typedEvent.senderId !== user?.id;
      const isSelected =
        state.messaging.selectedConversationId ===
        typedEvent.payload.conversationId;

      if (!conv) {
        if (isSenderNotMe) {
          dispatch(setHasUnreadMessages(true));
          dispatch(fetchInboxSummary());
        }
        return;
      }

      const newMsg: ChatMessage = {
        id: typedEvent.id,
        conversationId: typedEvent.payload.conversationId,
        senderId: typedEvent.senderId,
        text: typedEvent.payload.text,
        messageType: MessageType.TEXT,
        sentAt: typedEvent.timestamp,
        sender: typedEvent.payload.sender,
      };
      dispatch(
        setMessages({
          conversationId: newMsg.conversationId,
          message: newMsg,
        }),
      );

      const isUnassigned = !conv.assigneeId;
      const isAssignedToMe = conv?.assigneeId === user?.id;

      // do not notify if the message is sent by the current user or current view is messaging
      // do not notify other ASPA users if the conversation is assigned to another user
      const shouldNotify =
        isSenderNotMe && !isMessagingPath() && (isUnassigned || isAssignedToMe);

      if (isSenderNotMe && !isSelected) {
        dispatch(setHasUnreadMessages(true));
        dispatch(fetchInboxSummary());
      }

      if (shouldNotify) {
        sendMessage(t("messaging:newMessage"), "success");
      }
      break;
    }
    case MessagingEventType.CONVERSATION_MESSAGES_READ: {
      const typedEvent = event as SocketEvent<ConversationMessagesReadPayload>;
      const { conversationId, readerId, lastReadMessageId } =
        typedEvent.payload;
      const state = getState();
      const messages = selectAllMessages(state, conversationId);
      const plan = planReadReceipt(
        messages,
        user.id,
        readerId,
        lastReadMessageId,
      );
      if (plan.action === "ignore") break;
      // The read boundary is not loaded, or two messages share a sentAt.
      // Reload the newest page so readBy comes from the server.
      if (plan.action === "refetch") {
        const isOpen =
          state.messaging.selectedConversationId === conversationId;
        if (isOpen || messages.length > 0) {
          dispatch(
            fetchConversationMessages({ conversationId, isInitial: true }),
          );
        }
        break;
      }
      if (plan.messageIds.length > 0) {
        dispatch(
          applyReadReceipt({
            conversationId,
            readerId,
            messageIds: plan.messageIds,
          }),
        );
      }
      break;
    }
    case MessagingEventType.CONVERSATION_MESSAGE_ATTACHMENTS_UPDATED: {
      const typedEvent =
        event as SocketEvent<ConversationMessageAttachmentsUpdatedPayload>;
      dispatch(
        applyServerAttachments({
          conversationId: typedEvent.payload.conversationId,
          messageId: typedEvent.payload.messageId,
          attachments: typedEvent.payload.attachments,
        }),
      );
      break;
    }
    case MessagingEventType.NEW_CONVERSATION: {
      const typedEvent = event as SocketEvent<Conversation>;
      dispatch(upsertConversation(typedEvent.payload));
      const startedBySomeoneElse =
        typedEvent.payload.lastMessage?.senderId !== user?.id;
      if (startedBySomeoneElse) {
        dispatch(setHasUnreadMessages(true));
        if (!isMessagingPath()) {
          sendMessage(t("messaging:newMessage"), "success");
        }
      }
      dispatch(fetchInboxSummary());
      break;
    }
    case MessagingEventType.CONVERSATION_STATUS_UPDATED:
    case MessagingEventType.CONVERSATION_ASSIGNED: {
      const typedEvent = event as SocketEvent<
        ConversationAssignedPayload | ConversationStatusUpdatedPayload
      >;

      const isCurrentUser =
        typedEvent.type === MessagingEventType.CONVERSATION_STATUS_UPDATED
          ? (typedEvent.payload as ConversationStatusUpdatedPayload)
              .updatedBy === user.id
          : (typedEvent.payload as ConversationAssignedPayload).assignedBy ===
            user.id;

      // do nothing if the event is triggered by the current user
      if (isCurrentUser) break;

      if (
        typedEvent.type === MessagingEventType.CONVERSATION_STATUS_UPDATED &&
        (typedEvent.payload as ConversationStatusUpdatedPayload).status ===
          ConversationStatus.CLOSED
      ) {
        break;
      }

      dispatch(fetchInboxSummary());

      const refreshRequest = openInboxRefreshRequest(
        getState().messaging.inboxTabId,
      );
      if (!refreshRequest) break;

      sendMessage(t("messaging:conversationsUpdated"), "info");
      dispatch(fetchConversations(refreshRequest));
      dispatch(
        resetMessagesByConvId({
          conversationId: typedEvent.payload.conversationId,
        }),
      );
      break;
    }
    case MessagingEventType.CONVERSATION_TRANSFERRED: {
      const typedEvent = event as SocketEvent<ConversationTransferredPayload>;
      const { conversationId, destination, transferredBy } = typedEvent.payload;
      if (transferredBy === user.id) break;

      const state = getState();
      const isRecipient =
        destination.type === "EXPERT" && destination.ids[0] === user.id;
      const alreadyOpen =
        state.messaging.selectedConversationId === conversationId;

      dispatch(fetchInboxSummary());
      const refreshRequest = activeInboxRefreshRequest(
        state.messaging.inboxTabId,
      );
      if (refreshRequest) {
        dispatch(fetchConversations(refreshRequest));
        dispatch(resetMessagesByConvId({ conversationId }));
      }

      if (alreadyOpen) break;

      if (isRecipient) {
        const name = transferrerDisplayName(
          state.expertUsers?.entities?.[transferredBy],
        );
        sendMessage(
          name
            ? t("messaging:conversationTransferredToYou", { name })
            : t("messaging:conversationTransferredToYouFallback"),
          "success",
        );
        break;
      }

      if (!refreshRequest) break;
      sendMessage(t("messaging:conversationsUpdated"), "info");
      break;
    }
  }
};
